import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetAgingOverviewResponse,
    GetMetricsByRelationshipResponse
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    ErrorMessage,
    forLifeOf,
    getDynamicRouteLink,
    LifecycleAware,
    Table
} from '@lsq-frontend/common-ui';
import { ClaimService, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import type { ChartOptions } from 'chart.js';
import { startOfMonth } from 'date-fns';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@LifecycleAware()
@Component({
    selector: 'aging-summary',
    templateUrl: './aging-summary.component.html',
    styleUrls: ['./aging-summary.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AgingSummaryComponent implements OnInit, OnDestroy {
    @Input() public agingOverviewDataSource: LsqDataSource<GetAgingOverviewResponse>;
    @Input() public metricsDataSource?: LsqDataSource<GetMetricsByRelationshipResponse>;
    @Input() public debtorKey: string;

    public agingOverviewData$: Observable<GetAgingOverviewResponse>;
    public metricsData$: Observable<GetMetricsByRelationshipResponse>;
    public invoiceCount: number; // Needed for i18n.

    public chartOptions: ChartOptions = {
        cutoutPercentage: 60,
        aspectRatio: 1,
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const percentage = Math.floor(
                        (dataset.data[tooltipItem.index] as number) * 100
                    );
                    return data.labels[tooltipItem.index] + ': ' + percentage + '%';
                }
            }
        }
    };
    public colors = ['#008661', '#5667b1', '#ffd146', '#ff5841'];
    public blankChartColor = '#eef0f7';
    public labels = [];
    public chartData: number[];
    public startOfMonth = startOfMonth;
    public today = new Date();
    public isMobile = false;
    public icons = Icons;
    public color = Color;
    public nullAmount = Table.NullAmount;
    public dataSourceErrorCopy = ErrorMessage.dataSourceError;
    public tooltipPlacement = PopoverPlacement.Top;
    public tooltipText: string =
        "A summary of all your unpaid invoices and the length of time they've been outstanding.";

    constructor(
        private _responsiveService: ResponsiveService,
        private _router: Router,
        private _i18n: I18n,
        private _claimService: ClaimService
    ) {
        this.labels = [
            this._i18n({
                value: 'CURRENT',
                meaning: 'Current label for overview doughnut graph'
            }),
            this._i18n({
                value: '1-30 DAYS',
                meaning: '1-30 Days label for overview doughnut graph'
            }),
            this._i18n({
                value: '31-60 DAYS',
                meaning: '31-60 Days label for overview doughnut graph'
            }),
            this._i18n({
                value: '61+ DAYS',
                meaning: '61+ Days label for overview doughnut graph'
            })
        ];
    }

    public ngOnInit(): void {
        this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        if (this.agingOverviewDataSource) {
            this.agingOverviewData$ = this.agingOverviewDataSource.connect();
        }
        if (this.metricsDataSource) {
            this.metricsData$ = this.metricsDataSource.connect();
        }

        this.agingOverviewDataSource.data$
            .pipe(
                forLifeOf(this),
                filter((data) => !!data),
                map((agingOverviewData) =>
                    [
                        agingOverviewData.percentCurrent,
                        agingOverviewData.percentDayOne,
                        agingOverviewData.percentDayThirtyOne,
                        agingOverviewData.percentDaySixtyOne
                    ].map((datum) => (datum > 0 ? Math.max(0.01, datum) : 0))
                )
            )
            .subscribe((data) => (this.chartData = data));

        // Necessary for i18n.
        this.agingOverviewData$.pipe(forLifeOf(this)).subscribe(({ invoiceCount }) => {
            this.invoiceCount = invoiceCount;
        });
    }

    public ngOnDestroy(): void {
        if (this.agingOverviewDataSource) {
            this.agingOverviewDataSource.disconnect();
        }
        if (this.metricsDataSource) {
            this.metricsDataSource.disconnect();
        }
    }

    public getCustomerLink(link: string): string {
        return getDynamicRouteLink(link, { debtorKey: this.debtorKey });
    }

    public goToLinkOnMobile(dest: string, queryParams = {}): void {
        const canViewReports = this._claimService.isOnSync(DashboardClaim.ViewReports);
        if (canViewReports) {
            if (this.isMobile) {
                // Using 'navigate' because 'navigateByUrl' strips the queryParams if they
                // aren't in the link string as well
                this._router.navigate([dest], { queryParams });
            }
        }
    }
}
